import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import DataTable from '../../components/ui/data-table';
import EmptyState from '../../components/ui/empty-state.jsx';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { libraryService } from '../../services/libraryService';
import { 
  Library, 
  Search, 
  Filter, 
  Download, 
  Eye, 
  Calendar,
  FileText,
  Image,
  Video,
  Music,
  Archive
} from 'lucide-react';

const LibraryPage = () => {
  const [resources, setResources] = React.useState([]);
  const [filteredResources, setFilteredResources] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [typeFilter, setTypeFilter] = React.useState('all');
  const [categoryFilter, setCategoryFilter] = React.useState('all');
  const [sortBy, setSortBy] = React.useState('title');
  const [viewMode, setViewMode] = React.useState('grid'); // grid or table

  const fetchResources = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await libraryService.getResources();
      // Handle different response structures
      const resourcesData = res.data?.data?.resources || res.data?.data || [];
      setResources(Array.isArray(resourcesData) ? resourcesData : []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (resourceId) => {
    try {
      // Get presigned URL from backend
      const response = await libraryService.downloadResource(resourceId);
      const downloadUrl = response.data?.data?.downloadUrl || response.data?.downloadUrl;
      
      if (downloadUrl) {
        // Create a temporary link to trigger download
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.download = response.data?.data?.filename || 'resource';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // Fallback: try direct download endpoint
        const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1';
        const baseUrl = apiBaseUrl.replace('/api/v1', '');
        const fallbackUrl = `${baseUrl}/api/v1/library/resources/${resourceId}/download`;
        window.open(fallbackUrl, '_blank');
      }
      
      // Refresh resources to update download count
      fetchResources();
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download resource. Please try again.');
    }
  };

  React.useEffect(() => {
    fetchResources();
  }, []);

  // Filter and search resources
  React.useEffect(() => {
    const resourcesArray = Array.isArray(resources) ? resources : [];
    let filtered = [...resourcesArray];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(resource =>
        resource.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.category?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(resource => resource.type === typeFilter);
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(resource => resource.category === categoryFilter);
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'title':
          return (a.title || '').localeCompare(b.title || '');
        case 'downloads':
          return (b.downloadCount || 0) - (a.downloadCount || 0);
        case 'date':
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        default:
          return 0;
      }
    });

    setFilteredResources(filtered);
  }, [resources, searchTerm, typeFilter, categoryFilter, sortBy]);

  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf': return <FileText className="w-5 h-5 text-red-500" />;
      case 'image': return <Image className="w-5 h-5 text-blue-500" />;
      case 'video': return <Video className="w-5 h-5 text-purple-500" />;
      case 'audio': return <Music className="w-5 h-5 text-green-500" />;
      case 'archive': return <Archive className="w-5 h-5 text-orange-500" />;
      default: return <FileText className="w-5 h-5 text-gray-500" />;
    }
  };

  const getUniqueCategories = () => {
    const resourcesArray = Array.isArray(resources) ? resources : [];
    const categories = [...new Set(resourcesArray.map(r => r.category).filter(Boolean))];
    return categories;
  };

  const getUniqueTypes = () => {
    const resourcesArray = Array.isArray(resources) ? resources : [];
    const types = [...new Set(resourcesArray.map(r => r.type).filter(Boolean))];
    return types;
  };

  const columns = [
    { 
      id: 'title', 
      header: 'Title', 
      accessorKey: 'title', 
      sortable: true,
      cell: ({ row }) => (
        <div className="flex items-center space-x-2">
          {getFileIcon(row.type)}
          <span className="font-medium">{row.title}</span>
        </div>
      )
    },
    { 
      id: 'type', 
      header: 'Type', 
      accessorKey: 'type', 
      sortable: true,
      cell: ({ row }) => (
        <Badge variant="outline">{row.type}</Badge>
      )
    },
    { 
      id: 'category', 
      header: 'Category', 
      accessorKey: 'category',
      cell: ({ row }) => (
        <Badge variant="secondary">{row.category}</Badge>
      )
    },
    { 
      id: 'downloads', 
      header: 'Downloads', 
      accessorKey: 'downloadCount', 
      sortable: true,
      cell: ({ row }) => (
        <div className="flex items-center space-x-1">
          <Download className="w-4 h-4 text-gray-500" />
          <span>{row.downloadCount || 0}</span>
        </div>
      )
    },
    {
      id: 'actions', 
      header: 'Actions', 
      cell: ({ row }) => (
        <div className="flex space-x-2">
          <Button size="sm" variant="outline" onClick={() => window.location.assign(`/dashboard/library/${row.id}`)}>
            <Eye className="w-4 h-4 mr-1" />
            View
          </Button>
          <Button size="sm" onClick={() => handleDownload(row.id)}>
            <Download className="w-4 h-4 mr-1" />
            Download
          </Button>
        </div>
      )
    }
  ];

  if (loading) return <LoadingSpinner />;
  if (error) return (
    <div className="p-6">
      <Card>
        <CardContent className="p-6">
          <p className="text-red-600 text-sm">{String(error)}</p>
          <Button className="mt-3" onClick={fetchResources}>Retry</Button>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Digital Library</h1>
          <p className="text-gray-600 mt-1">Access educational resources, past questions, and learning materials</p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={() => setViewMode(viewMode === 'grid' ? 'table' : 'grid')}>
            {viewMode === 'grid' ? 'Table View' : 'Grid View'}
          </Button>
          <Button onClick={fetchResources}>
            <Library className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Search className="w-5 h-5 mr-2" />
            Search & Filter Resources
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Input
                placeholder="Search resources..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>
            <div>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {getUniqueTypes().map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {getUniqueCategories().map(category => (
                    <SelectItem key={category} value={category}>{category}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger>
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="title">Title</SelectItem>
                  <SelectItem value="downloads">Downloads</SelectItem>
                  <SelectItem value="date">Date Added</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Summary */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Showing {filteredResources.length} of {resources.length} resources
        </p>
        <div className="flex space-x-2">
          <Badge variant="outline">{resources.length} Total</Badge>
          <Badge variant="secondary">{filteredResources.length} Filtered</Badge>
        </div>
      </div>

      {/* Resources Display */}
      {filteredResources.length === 0 ? (
        <EmptyState 
          icon={Library}
          title="No resources found"
          description={searchTerm || typeFilter !== 'all' || categoryFilter !== 'all' 
            ? "Try adjusting your search criteria or filters." 
            : "Library resources will appear here when they are added."}
          action
          actionLabel="Clear Filters"
          onAction={() => {
            setSearchTerm('');
            setTypeFilter('all');
            setCategoryFilter('all');
          }}
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            {viewMode === 'table' ? (
              <DataTable data={filteredResources} columns={columns} searchable />
            ) : (
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredResources.map((resource) => (
                    <Card key={resource.id} className="hover:shadow-lg transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          {getFileIcon(resource.type)}
                          <Badge variant="outline">{resource.type}</Badge>
                        </div>
                        <h3 className="font-semibold text-lg mb-2 line-clamp-2">{resource.title}</h3>
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{resource.description}</p>
                        <div className="flex items-center justify-between mb-3">
                          <Badge variant="secondary">{resource.category}</Badge>
                        </div>
                        <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                          <div className="flex items-center space-x-1">
                            <Download className="w-4 h-4" />
                            <span>{resource.downloadCount || 0}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Calendar className="w-4 h-4" />
                            <span>{new Date(resource.createdAt).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="flex-1"
                            onClick={() => window.location.assign(`/dashboard/library/${resource.id}`)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          <Button 
                            size="sm" 
                            className="flex-1"
                            onClick={() => handleDownload(resource.id)}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Download
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LibraryPage;


